// ТРАНСПОРТ ОТПРАВКИ ШАГА 7 — «Send it to me» → письмо с Life Map. B9-4.
//
// ФОРМА — по образцу `src/quotes/transport.js` (инъекция `fetchImpl`, `AbortSignal` + гонка с таймером,
// `classifyHttpStatus`), но с двумя отличиями, и оба — буква тикета (DoR s962, сводка s932):
//
//   · ПОВТОРОВ НЕТ (`maxRetries: 0`), `withRetry` не зовётся. Цитаты повторяют молча, потому что их
//     никто не ждёт; здесь человек уже видит «On its way to …», а повтор за его спиной значил бы два
//     письма на одно нажатие. Ретрай — это сама кнопка send, вернувшаяся на место (E2, `s921.d1`);
//
//   · НЕ БРОСАЕТ НИКОГДА. Исход — `{ sent, reason }`: экран читает только `sent`, `reason` — для лога
//     и тестов. Любой сбой (5xx, 4xx, сетевой throw, таймаут, нет адреса сервиса) → `sent: false`.
//     Различать их экрану незачем: на всё один и тот же E2 (`s921.d1`–`d4`).
//
// ⭐ АДРЕС СЕРВИСА — ПАРАМЕТР, А НЕ КОНСТАНТА. Сервис — `/api/send-email` (Setup Resend, часть 1); в
// приложении адрес берётся из `VITE_SEND_ENDPOINT` (`sendEndpoint()` ниже) и в Vercel пока не задан —
// то есть каждая отправка честно даёт `sent: false` → E2. Это ожидаемое состояние до части 2 (ключ,
// отправитель, адрес в окружении сборки), а не поломка.
//
// ⚠️ ТЕЛО ЗАПРОСА: `{ email, idempotencyKey, token }` — адрес, ключ тапа (`tapKey()`, Setup Resend
// `s973.d1`) и токен уже сохранённого слепка (Supabase, часть 1; `sendWithSnapshot` в `StepSeven.jsx`).
// Ввода конверта в теле нет с cc-207: письму нужна только ссылка. Адрес в `reason` НЕ кладётся (`s946.d3`).
//
// ⛔ ЖИВЁТ ВНЕ `src/wizard/`: голого `fetch` в визарде нет, экран зовёт эту функцию, а `fetchImpl`
// подставляется (по умолчанию — `globalThis.fetch` в момент вызова, не импорта: тесты подменяют его).

import { classifyHttpStatus } from '../quotes/transport.js';

/** Верхняя граница одной (и единственной) попытки — та же, что у транспорта цитат (cc-31). */
export const SEND_TIMEOUT_MS = 30_000;

/**
 * Адрес сервиса отправки из окружения сборки. Читается в момент вызова, а не импорта: так его
 * подменяет `vi.stubEnv` в тестах. Пустая строка и отсутствие — одно и то же «адреса нет».
 */
export function sendEndpoint() {
  const v = import.meta.env?.VITE_SEND_ENDPOINT;
  return typeof v === 'string' && v.trim() !== '' ? v.trim() : null;
}

/**
 * Ключ идемпотентности ОДНОГО тапа send (Setup Resend `s973.d1`): функция отправки передаёт его
 * заголовком `Idempotency-Key` в Resend, и тот же тап второго письма не даст. Новый тап — новый ключ.
 * Ключ едет только в теле запроса и в конверт не попадает (`SCHEMA_VERSION` не тронут).
 * `crypto` подставляется в тестах; без `randomUUID` (старый браузер) — запасной случайный ключ.
 */
export function tapKey(cryptoImpl = globalThis.crypto) {
  if (typeof cryptoImpl?.randomUUID === 'function') return cryptoImpl.randomUUID();
  return `tap-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}

/**
 * Одна отправка, без повторов. Никогда не бросает.
 *
 * @param {{email:string, idempotencyKey?:string, token?:string}} input
 * @param {{endpoint?:string|null, fetchImpl?:Function, timeoutMs?:number}} [opts]
 * @returns {Promise<{sent:boolean, reason:string}>}
 */
export async function sendPassport(input, { endpoint, fetchImpl = globalThis.fetch, timeoutMs = SEND_TIMEOUT_MS } = {}) {
  if (!endpoint) return { sent: false, reason: 'no_endpoint' };
  if (typeof fetchImpl !== 'function') return { sent: false, reason: 'no_fetch' };

  // Граница держится двумя вещами, как у цитат: `signal` действительно рвёт запрос, а гонка с
  // таймером держит границу, даже если реализация сигнал не слушает (тест с висящим fetch).
  const bounded = timeoutMs > 0 && typeof AbortController !== 'undefined';
  const ac = bounded ? new AbortController() : null;
  let timer = null;
  const TIMEOUT = Symbol('timeout');
  try {
    const call = fetchImpl(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
      ...(ac ? { signal: ac.signal } : {}),
    });
    const resp = bounded
      ? await Promise.race([
        call,
        new Promise((resolve) => {
          timer = setTimeout(() => {
            ac.abort();
            resolve(TIMEOUT);
          }, timeoutMs);
        }),
      ])
      : await call;
    if (resp === TIMEOUT) return { sent: false, reason: `timeout_${timeoutMs}ms` };
    if (!resp || !resp.ok) {
      const status = resp?.status ?? null;
      // Класс статуса — только для лога: повтора нет ни у временного, ни у постоянного.
      return { sent: false, reason: `http_${status}_${status == null ? 'unknown' : classifyHttpStatus(status)}` };
    }
    return { sent: true, reason: 'ok' };
  } catch (err) {
    const aborted = ac?.signal.aborted || err?.name === 'AbortError';
    return { sent: false, reason: aborted ? `timeout_${timeoutMs}ms` : 'network' };
  } finally {
    // Снять обязательно: иначе таймер держит event loop и на успешном ответе.
    if (timer) clearTimeout(timer);
  }
}
